import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';

interface Sector {
  id: string;
  name_ar: string;
  name_en: string;
  description_ar?: string;
  description_en?: string;
  short_description_ar?: string;
  short_description_en?: string;
  slug: string;
  icon_url?: string;
  featured_image_url?: string;
  gallery_urls?: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Simple, fast sectors hook - no complex queries, no company counts
export const useSimpleSectors = () => {
  return useQuery({
    queryKey: ['sectors', 'simple'],
    queryFn: async (): Promise<Sector[]> => {
      // If Supabase isn't configured (e.g., local preview without env), return empty list safely
      if (!isSupabaseConfigured || !supabase) {
        return [];
      }
      try {
        const { data, error } = await supabase
          .from('sectors')
          .select(
            'id, name_ar, name_en, description_ar, description_en, short_description_ar, short_description_en, slug, icon_url, featured_image_url, is_active, sort_order, created_at, updated_at'
          )
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) {
          console.error('Sectors query error:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('useSimpleSectors error:', error);
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - longer cache for stable data
    gcTime: 15 * 60 * 1000, // 15 minutes cache
    retry: 3, // More retries for critical data
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    enabled: true,
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  });
};